import { FlightFilterModel } from 'ts/flight/classes/FlightFilterModel';
import { FlightSearch, Outbound } from 'ts/flight/interfaces/FlightSearch';
import { format } from 'date-fns';
import { getBounds } from './flight';

export const checkNoBaggage = (outbound: Outbound) => {
  if (!outbound) return true;
  if (
    !outbound.listOfFlyBaggagePrice ||
    !outbound.listOfFlyBaggagePrice.length ||
    !outbound.listOfFlyBaggagePrice?.[0]
  ) {
    return true;
  }
  return (
    outbound.listOfFlyBaggagePrice[0].maxTotalWeight <= 0 &&
    outbound.listOfFlyBaggagePrice[0].pieces === 0
  );
};
export const filterOutbound = (flights: FlightSearch[], filter: FlightFilterModel) => {
  return flights.map((flight) => {
    const bounds = getBounds(flight);
    const checkedStops = filter.stops.filter((stop) => stop.checked);
    const filteredByStops =
      checkedStops.length === 0 ||
      checkedStops.some((stop) => {
        const outbondStopsCount = flight.outbound?.legCount - 1;
        const inboundStopsCount = flight.inbound?.legCount - 1;
        if (isNaN(inboundStopsCount)) {
          return stop.number === outbondStopsCount;
        } else {
          if (stop.number === 0.5) {
            return outbondStopsCount === 0 && inboundStopsCount > 0;
          } else {
            return stop.number === outbondStopsCount && stop.number === inboundStopsCount;
          }
        }
      });

    let filteredByBaggage = true;
    if (filter.baggage[1].checked && filter.baggage[0].checked) {
    } else if (filter.baggage[0].checked) {
      filteredByBaggage = checkNoBaggage(flight.outbound) && checkNoBaggage(flight.inbound);
    } else if (filter.baggage[1].checked) {
      filteredByBaggage = !checkNoBaggage(flight.outbound) || !checkNoBaggage(flight.inbound);
    }

    const filteredByPrice =
      filter.price.value[0] <= flight.flighPrice.grossAndTaxWithMarkup.value &&
      filter.price.value[1] >= flight.flighPrice.grossAndTaxWithMarkup.value;

    /// TIME  ///
    let filteredByDepartureTime = true;
    let filteredByArrivalTime = true;
    if (filter.departureOutboundTime && filter.arrivalOutboundTime) {
      const departureOutboundTime = new Date(flight.outbound.departureDateTime).getTime();
      filteredByDepartureTime =
        filter.departureOutboundTime.value[0] <= departureOutboundTime &&
        filter.departureOutboundTime.value[1] >= departureOutboundTime;

      const arrivalOutboundTime = new Date(flight.outbound.arrivalDateTime).getTime();
      filteredByArrivalTime =
        filter.arrivalOutboundTime.value[0] <= arrivalOutboundTime &&
        filter.arrivalOutboundTime.value[1] >= arrivalOutboundTime;
    }

    let filteredByDepartureInboundTime = true;
    let filteredByArrivalInboundTime = true;
    if (filter.isInbound && filter.departureInboundTime && filter.arrivalInboundTime) {
      const departureInboundTime = new Date(flight.inbound?.departureDateTime).getTime();
      filteredByDepartureInboundTime =
        filter.departureInboundTime.value[0] <= departureInboundTime &&
        filter.departureInboundTime.value[1] >= departureInboundTime;

      const arrivalInboundTime = new Date(flight.inbound?.arrivalDateTime).getTime();
      filteredByArrivalInboundTime =
        filter.arrivalInboundTime.value[0] <= arrivalInboundTime &&
        filter.arrivalInboundTime.value[1] >= arrivalInboundTime;
    }

    /// DURATION ///
    const filteredByDuration = bounds.some(
      (b: any) =>
        !b.elapsedDTime ||
        (filter.duration.value[0] <= b.elapsedDTime && filter.duration.value[1] >= b.elapsedDTime),
    );

    const filteredByStopDuration = bounds.some(
      (b: any) =>
        !b.maxConnDTime ||
        (filter.stopDuration.value[0] <= b.maxConnDTime &&
          filter.stopDuration.value[1] >= b.maxConnDTime),
    );

    const checkedAirlines = filter.airlines.filter((airline) => airline.checked);
    const filteredByAirline =
      !checkedAirlines.length ||
      checkedAirlines.some((airline) => airline.code === flight.airlineCode);

    const checkedAirports = filter.airports.filter((airport) => airport.checked);
    const filteredByAirport =
      !checkedAirports.length ||
      checkedAirports.some(
        (airport) =>
          airport.code === flight.outbound.departureAirport.code ||
          airport.code === flight.outbound.arrivalAirport.code ||
          airport.code === flight.inbound?.departureAirport.code ||
          airport.code === flight.inbound?.arrivalAirport.code,
      );

    const checkedFlightTypes = filter.flightTypes.filter((flightType) => flightType.checked);
    const filteredByFlightType =
      !checkedFlightTypes.length ||
      checkedFlightTypes.some((flightType) => flightType.label === flight.flightSource);

    // Text filtering
    const filteredByText =
      !filter.freeText ||
      (() => {
        const searchTexts = filter.freeText.toLowerCase().split(' ');
        const boundsTexts = bounds
          .map((bound: any) => {
            return [
              flight.airlineCode + ' ' + bound.flightNumber,
              format(new Date(bound.departureDateTime), 'dd/MM'),
              bound.departureAirport.code,
              bound.arrivalAirport.code,
              format(new Date(bound.departureDateTime), 'HH:mm'),
              format(new Date(bound.arrivalDateTime), 'HH:mm'),
              bound.elapsedTime,
              ...(bound.gdsFlyAirs?.map((air: any) => air.departureAirport.code) || []),
              bound.stopQuantity === 0 ? 'Direct' : bound.connTime,
              `${bound.class?.code || ''}${bound.availSeatsNo}`,
              bound.cabin?.desc,
              bound.listOfFlyBaggagePrice?.[0]?.pieces?.toString(),
            ]
              .filter(Boolean)
              .join(' ');
          })
          .join(' ')
          .toLowerCase();

        const searchableFields =
          [
            flight.flightSource,
            flight.fareFamilyInfo?.[0]?.desc,
            flight.provider,
            Math.round(flight.flighPrice.grossAndTaxWithMarkup.value).toString(),
          ]
            .filter(Boolean)
            .map((field) => field?.toLowerCase())
            .join(' ') + boundsTexts;

        return searchTexts.every((searchText) => searchableFields.includes(searchText));
      })();

    const visible =
      filteredByStops &&
      filteredByBaggage &&
      filteredByPrice &&
      filteredByDepartureTime &&
      filteredByArrivalTime &&
      filteredByDepartureInboundTime &&
      filteredByArrivalInboundTime &&
      filteredByDuration &&
      filteredByStopDuration &&
      filteredByAirline &&
      filteredByAirport &&
      filteredByFlightType &&
      filteredByText;

    return {
      ...flight,
      visible,
    };
  });
};

export const filterMulti = (flights: FlightSearch[], filter: FlightFilterModel) => {
  return flights.map((flight) => {
    const checkedStops = filter.stops.filter((stop) => stop.checked);
    const filteredByStops =
      checkedStops.length === 0 ||
      checkedStops.some((stop) => {
        if (stop.number === 2) {
          return flight.flightBounds.every((bound) => bound.legCount - 1 >= stop.number);
        } else {
          return flight.flightBounds.every((bound) => bound.legCount - 1 === stop.number);
        }
      });

    let filteredByBaggage = true;
    if (filter.baggage[1].checked && filter.baggage[0].checked) {
    } else if (filter.baggage[0].checked) {
      filteredByBaggage = flight.flightBounds.every((bound) => checkNoBaggage(bound));
    } else if (filter.baggage[1].checked) {
      filteredByBaggage = flight.flightBounds.some((bound) => !checkNoBaggage(bound));
    }

    const filteredByPrice =
      filter.price.value[0] <= flight.flighPrice.grossAndTaxWithMarkup.value &&
      filter.price.value[1] >= flight.flighPrice.grossAndTaxWithMarkup.value;
    const filteredByDuration = flight.flightBounds.some(
      (bound) =>
        !bound.elapsedDTime ||
        (bound.elapsedDTime >= filter.duration.value[0] &&
          bound.elapsedDTime <= filter.duration.value[1]),
    );

    const filteredByStopDuration = flight.flightBounds.some(
      (bound) =>
        !bound.maxConnDTime ||
        (bound.maxConnDTime >= filter.stopDuration.value[0] &&
          bound.maxConnDTime <= filter.stopDuration.value[1]),
    );

    const checkedAirlines = filter.airlines.filter((airline) => airline.checked);
    const filteredByAirline =
      !checkedAirlines.length ||
      checkedAirlines.some((airline) => airline.code === flight.airlineCode);

    const checkedAirports = filter.airports.filter((airport) => airport.checked);
    const filteredByAirport =
      !checkedAirports.length ||
      checkedAirports.some((airport) =>
        flight.flightBounds.some(
          (bound) =>
            airport.code === bound.departureAirport.code ||
            airport.code === bound.arrivalAirport.code,
        ),
      );

    const visible =
      filteredByStops &&
      filteredByBaggage &&
      filteredByPrice &&
      filteredByDuration &&
      filteredByStopDuration &&
      filteredByAirline &&
      filteredByAirport;

    return {
      ...flight,
      visible,
    };
  });
};
